import { useQuery } from '@tanstack/react-query';
import getApi from '@/lib/axios';
import { ExtractFnReturnType } from '@/lib/react-query';
import type {
  KpisResponse,
  MediaDisciplinaItem,
  HeatmapResponse,
  RadarResponse,
  EvolucaoBimestralItem,
  ResumoTurma,
  ScorePedagogicoFilters,
} from '../types';

// ── Helpers ──

type FilterParams = {
  serie_nome?: string;
  turma?: string;
  disciplina_id?: number;
  ano: number;
};

function buildParams(filters: ScorePedagogicoFilters): FilterParams {
  const params: FilterParams = { ano: filters.ano };
  if (filters.serieNome) params.serie_nome = filters.serieNome;
  if (filters.turmaNome) params.turma = filters.turmaNome;
  if (filters.disciplinaId) params.disciplina_id = filters.disciplinaId;
  return params;
}

// ── 1. KPIs ──

async function getKpis(filters: FilterParams): Promise<KpisResponse> {
  const api = await getApi();
  const { data } = await api.get<KpisResponse>('/score-pedagogico/kpis/', {
    params: filters,
  });
  return data;
}

type KpisQueryFn = typeof getKpis;

export function useScoreKpis(filters: ScorePedagogicoFilters) {
  const params = buildParams(filters);
  return useQuery<ExtractFnReturnType<KpisQueryFn>>({
    queryKey: ['score-pedagogico-kpis', params],
    queryFn: () => getKpis(params),
  });
}

// ── 2. Média por Disciplina ──

async function getMediaPorDisciplina(
  filters: FilterParams
): Promise<MediaDisciplinaItem[]> {
  const api = await getApi();
  const { data } = await api.get<MediaDisciplinaItem[]>(
    '/score-pedagogico/media-por-disciplina/',
    { params: filters }
  );
  return data;
}

type MediaDiscQueryFn = typeof getMediaPorDisciplina;

export function useMediaPorDisciplina(filters: ScorePedagogicoFilters) {
  const params = buildParams(filters);
  return useQuery<ExtractFnReturnType<MediaDiscQueryFn>>({
    queryKey: ['score-pedagogico-media-disciplina', params],
    queryFn: () => getMediaPorDisciplina(params),
  });
}

// ── 3. Heatmap ──

async function getHeatmap(filters: FilterParams): Promise<HeatmapResponse> {
  const api = await getApi();
  const { data } = await api.get<HeatmapResponse>(
    '/score-pedagogico/heatmap/',
    { params: filters }
  );
  return data;
}

type HeatmapQueryFn = typeof getHeatmap;

export function useHeatmap(filters: ScorePedagogicoFilters) {
  const params = buildParams(filters);
  return useQuery<ExtractFnReturnType<HeatmapQueryFn>>({
    queryKey: ['score-pedagogico-heatmap', params],
    queryFn: () => getHeatmap(params),
  });
}

// ── 4. Radar ──

async function getRadar(
  turma: string,
  ano: number
): Promise<RadarResponse> {
  const api = await getApi();
  const { data } = await api.get<RadarResponse>('/score-pedagogico/radar/', {
    params: { turma, ano },
  });
  return data;
}

type RadarQueryFn = typeof getRadar;

export function useRadar(turmaNome: string | null, ano: number) {
  return useQuery<ExtractFnReturnType<RadarQueryFn>>({
    queryKey: ['score-pedagogico-radar', turmaNome, ano],
    queryFn: () => getRadar(turmaNome!, ano),
    enabled: !!turmaNome,
  });
}

// ── 5. Evolução Bimestral ──

async function getEvolucaoBimestral(
  filters: FilterParams
): Promise<EvolucaoBimestralItem[]> {
  const api = await getApi();
  const { data } = await api.get<EvolucaoBimestralItem[]>(
    '/score-pedagogico/evolucao-bimestral/',
    { params: filters }
  );
  return data;
}

type EvolucaoQueryFn = typeof getEvolucaoBimestral;

export function useEvolucaoBimestral(filters: ScorePedagogicoFilters) {
  const params = buildParams(filters);
  return useQuery<ExtractFnReturnType<EvolucaoQueryFn>>({
    queryKey: ['score-pedagogico-evolucao', params],
    queryFn: () => getEvolucaoBimestral(params),
  });
}

// ── 6. Ranking de Turmas ──

async function getRankingTurmas(
  filters: FilterParams
): Promise<ResumoTurma[]> {
  const api = await getApi();
  const { data } = await api.get<ResumoTurma[]>(
    '/score-pedagogico/ranking-turmas/',
    { params: filters }
  );
  return data;
}

type RankingQueryFn = typeof getRankingTurmas;

export function useRankingTurmas(filters: ScorePedagogicoFilters) {
  const params = buildParams(filters);
  return useQuery<ExtractFnReturnType<RankingQueryFn>>({
    queryKey: ['score-pedagogico-ranking', params],
    queryFn: () => getRankingTurmas(params),
  });
}
